/**
 * Performance Monitoring Utility
 * Helps track DOM efficiency, memory usage, and spot management
 */

import { LOGGING } from '/settings.js';

class PerformanceMonitor {
    constructor() {
        this.metrics = {
            markersCreated: 0,
            markersRemoved: 0,
            spotsLoaded: 0,
            memorySnapshots: [],
            domOperations: []
        };
        this.startTime = performance.now();
    }

    /**
     * Log marker creation
     */
    logMarkerCreated(spotId) {
        if (!(LOGGING.ENABLED && LOGGING.DEBUG)) return;

        // Check if this is a batch operation (clustering)
        if (typeof spotId === 'string' && spotId.startsWith('batch_')) {
            const batchSize = parseInt(spotId.split('_')[1]);
            this.metrics.markersCreated += batchSize;
            this.logDOMOperation('marker_batch_created', {
                batchSize,
                totalMarkers: this.metrics.markersCreated,
                operationType: 'clustered'
            });
        } else {
            // Individual marker
            this.metrics.markersCreated++;
            this.logDOMOperation('marker_created', {
                spotId,
                totalMarkers: this.metrics.markersCreated,
                operationType: 'individual'
            });
        }
    }

    /**
     * Log marker removal
     */
    logMarkerRemoved(spotId) {
        if (!(LOGGING.ENABLED && LOGGING.DEBUG)) return;
        this.metrics.markersRemoved++;
        this.logDOMOperation('marker_removed', { spotId, totalMarkers: this.metrics.markersCreated - this.metrics.markersRemoved });
    }

    /**
     * Log spots loaded
     */
    logSpotsLoaded(count) {
        if (!(LOGGING.ENABLED && LOGGING.DEBUG)) return;
        this.metrics.spotsLoaded += count;
        this.logDOMOperation('spots_loaded', { count, totalSpots: this.metrics.spotsLoaded });
    }

    /**
     * Take a memory snapshot
     */
    takeMemorySnapshot(label = 'snapshot') {
        if (!(LOGGING.ENABLED && LOGGING.DEBUG)) return;
        if (performance.memory) {
            const snapshot = {
                label,
                timestamp: performance.now(),
                usedJSHeapSize: performance.memory.usedJSHeapSize,
                totalJSHeapSize: performance.memory.totalJSHeapSize,
                jsHeapSizeLimit: performance.memory.jsHeapSizeLimit
            };
            this.metrics.memorySnapshots.push(snapshot);

            if (LOGGING.ENABLED && LOGGING.DEBUG) {
                console.log(`[Performance] Memory ${label}:`, {
                    used: this.formatBytes(snapshot.usedJSHeapSize),
                    total: this.formatBytes(snapshot.totalJSHeapSize),
                    limit: this.formatBytes(snapshot.jsHeapSizeLimit)
                });
            }
        }
    }

    /**
     * Log DOM operations
     */
    logDOMOperation(operation, data = {}) {
        if (!(LOGGING.ENABLED && LOGGING.DEBUG)) return;
        const logEntry = {
            operation,
            timestamp: performance.now(),
            data
        };
        this.metrics.domOperations.push(logEntry);

        if (LOGGING.ENABLED && LOGGING.DEBUG) {
            console.log(`[Performance] DOM ${operation}:`, data);
        }
    }

    /**
     * Log clustering performance
     */
    logClusteringPerformance(markersCount, clustersCount, timeMs) {
        if (!(LOGGING.ENABLED && LOGGING.DEBUG)) return;
        const efficiency = clustersCount > 0 ? (markersCount / clustersCount).toFixed(2) : 'N/A';
        console.log(`[Performance] Clustering: ${markersCount} markers → ${clustersCount} clusters (${efficiency}x efficiency) in ${timeMs}ms`);
        this.logDOMOperation('clustering_performance', {
            markersCount,
            clustersCount,
            efficiency,
            timeMs
        });
    }

    /**
     * Get performance summary
     */
    getSummary() {
        const runtime = performance.now() - this.startTime;
        const summary = {
            runtime: this.formatTime(runtime),
            markersCreated: this.metrics.markersCreated,
            markersRemoved: this.metrics.markersRemoved,
            spotsLoaded: this.metrics.spotsLoaded,
            memorySnapshots: this.metrics.memorySnapshots.length,
            domOperations: this.metrics.domOperations.length
        };

        if (performance.memory) {
            const latestSnapshot = this.metrics.memorySnapshots[this.metrics.memorySnapshots.length - 1];
            if (latestSnapshot) {
                summary.currentMemory = this.formatBytes(latestSnapshot.usedJSHeapSize);
            }
        }

        return summary;
    }

    /**
     * Print performance summary to console
     */
    printSummary() {
        if (!(LOGGING.ENABLED && LOGGING.DEBUG)) return;
        const summary = this.getSummary();
        console.group('🚀 Performance Summary');
        console.log('Runtime:', summary.runtime);
        console.log('Markers Created:', summary.markersCreated);
        console.log('Markers Removed:', summary.markersRemoved);
        console.log('Active Markers:', summary.markersCreated - summary.markersRemoved);
        console.log('Spots Loaded:', summary.spotsLoaded);
        console.log('DOM Operations:', summary.domOperations);

        // Calculate clustering efficiency
        const clusteredOps = this.metrics.domOperations.filter(op =>
            op.operation === 'marker_batch_created' || op.operation === 'clustering_performance'
        ).length;
        const individualOps = this.metrics.domOperations.filter(op =>
            op.operation === 'marker_created'
        ).length;

        if (clusteredOps > 0) {
            console.log('📊 Clustering Efficiency:');
            console.log(`  - Clustered Operations: ${clusteredOps}`);
            console.log(`  - Individual Operations: ${individualOps}`);
            console.log(`  - Efficiency Ratio: ${summary.markersCreated / summary.domOperations} markers per DOM operation`);
        }

        if (summary.currentMemory) {
            console.log('Current Memory:', summary.currentMemory);
        }

        // Add helpful context
        if (summary.markersCreated > 0 && summary.markersRemoved === 0) {
            console.log('💡 Note: This appears to be the first search - no leaks detected');
        } else if (summary.markersCreated - summary.markersRemoved > 0) {
            console.log('⚠️ Note: Some markers may not have been cleared');
        } else {
            console.log('✅ All markers appear to be properly managed');
        }

        console.groupEnd();
    }

    /**
     * Check for potential memory leaks
     */
    checkForLeaks() {
        if (!(LOGGING.ENABLED && LOGGING.DEBUG)) return [];
        const markersDiff = this.metrics.markersCreated - this.metrics.markersRemoved;
        const warnings = [];

        // Only warn about marker leaks if we've done some clearing operations
        // This prevents false positives on the first search
        if (markersDiff > 10 && this.metrics.markersRemoved > 0) {
            warnings.push(`Potential marker leak: ${markersDiff} markers not removed`);
        }

        // Only warn about DOM operations if we have a significant number
        if (this.metrics.domOperations.length > 200) {
            warnings.push('High number of DOM operations detected');
        }

        // Only warn about memory if it's consistently high across multiple snapshots
        if (performance.memory) {
            const latestSnapshot = this.metrics.memorySnapshots[this.metrics.memorySnapshots.length - 1];
            if (latestSnapshot && latestSnapshot.usedJSHeapSize > 100 * 1024 * 1024) { // 100MB
                warnings.push('High memory usage detected');
            }
        }

        if (warnings.length > 0) {
            console.warn('⚠️ Performance Warnings:', warnings);
        }

        return warnings;
    }

    /**
     * Format bytes to human readable
     */
    formatBytes(bytes) {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    /**
     * Format time to human readable
     */
    formatTime(ms) {
        if (ms < 1000) return `${ms.toFixed(0)}ms`;
        if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
        return `${(ms / 60000).toFixed(1)}m`;
    }

    /**
     * Reset all metrics
     */
    reset() {
        if (LOGGING.ENABLED && LOGGING.DEBUG) {
            console.log('🔄 Performance metrics reset');
        }
        this.metrics = {
            markersCreated: 0,
            markersRemoved: 0,
            spotsLoaded: 0,
            memorySnapshots: [],
            domOperations: []
        };
        this.startTime = performance.now();
    }
}

// Create global instance
const performanceMonitor = new PerformanceMonitor();

// Export functions for easy use
export function logMarkerCreated(spotId) {
    performanceMonitor.logMarkerCreated(spotId);
}

export function logMarkerRemoved(spotId) {
    performanceMonitor.logMarkerRemoved(spotId);
}

export function logSpotsLoaded(count) {
    performanceMonitor.logSpotsLoaded(count);
}

export function logClusteringPerformance(markersCount, clustersCount, timeMs) {
    performanceMonitor.logClusteringPerformance(markersCount, clustersCount, timeMs);
}

export function takeMemorySnapshot(label) {
    performanceMonitor.takeMemorySnapshot(label);
}

export function getPerformanceSummary() {
    return performanceMonitor.getSummary();
}

export function printPerformanceSummary() {
    performanceMonitor.printSummary();
}

export function checkForLeaks() {
    return performanceMonitor.checkForLeaks();
}

export function resetPerformanceMetrics() {
    performanceMonitor.reset();
}

// Auto-print summary on page unload
if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
        if (LOGGING.ENABLED && LOGGING.DEBUG) {
            performanceMonitor.printSummary();
            performanceMonitor.checkForLeaks();
        }
    });
} 